import setup from "./sdk-setup.json";

/** The quickstart's placeholder (docs/CLIENT_QUICKSTART.md): the gateway key
 * itself is shown once, in its own dialog, never in a snippet. */
export const KEY_PLACEHOLDER = "gw_YOUR_PHANTM_KEY";

/** The models the quickstart's snippets use. The wizard shows them too
 * whenever the account accepts them, so the two stay identical. */
const QUICKSTART_MODELS = { openai: "gpt-5.4-mini", anthropic: "claude-sonnet-4-6" };

export function snippetModels(accepted: { id: string; provider: string }[]): { openai: string; anthropic: string } {
  const pick = (provider: "openai" | "anthropic") => {
    const preferred = QUICKSTART_MODELS[provider];
    if (accepted.length === 0 || accepted.some((m) => m.id === preferred)) return preferred;
    return accepted.find((m) => m.provider === provider)?.id ?? preferred;
  };
  return { openai: pick("openai"), anthropic: pick("anthropic") };
}

export interface Snippet {
  id: "openai-python" | "openai-typescript" | "anthropic-python" | "anthropic-typescript" | "claude-code";
  label: string;
  code: string;
}

/** Copy-paste setup for each SDK and Claude Code. The base URLs and the key's
 * argument names come from sdk-setup.json, which tests/test_onboarding_wizard.py
 * sends real SDK requests with, so these work as written. */
export function buildSnippets(
  apiBase: string,
  models: { openai: string; anthropic: string },
  key: string = KEY_PLACEHOLDER,
): Snippet[] {
  const base = apiBase.replace(/\/+$/, "");
  const openaiUrl = base + setup.openai.path;
  const anthropicUrl = base + setup.anthropic.path;
  const { python_key_argument: openaiPyKey, typescript_key_option: openaiTsKey } = setup.openai;
  const { python_key_argument: anthropicPyKey, typescript_key_option: anthropicTsKey } = setup.anthropic;
  return [
    {
      id: "openai-python",
      label: "Python · OpenAI SDK",
      code: `from openai import OpenAI

client = OpenAI(
    base_url="${openaiUrl}",
    ${openaiPyKey}="${key}",
)
response = client.chat.completions.create(
    model="${models.openai}",
    messages=[{"role": "user", "content": "Say hello."}],
)
print(response.choices[0].message.content)`,
    },
    {
      id: "openai-typescript",
      label: "TypeScript · OpenAI SDK",
      code: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${openaiUrl}",
  ${openaiTsKey}: "${key}",
});
const response = await client.chat.completions.create({
  model: "${models.openai}",
  messages: [{ role: "user", content: "Say hello." }],
});
console.log(response.choices[0].message.content);`,
    },
    {
      id: "anthropic-python",
      label: "Python · Anthropic SDK",
      code: `import anthropic

client = anthropic.Anthropic(
    base_url="${anthropicUrl}",
    ${anthropicPyKey}="${key}",
)
message = client.messages.create(
    model="${models.anthropic}",
    max_tokens=1024,
    messages=[{"role": "user", "content": "Say hello."}],
)
print(message.content[0].text)`,
    },
    {
      id: "anthropic-typescript",
      label: "TypeScript · Anthropic SDK",
      code: `import Anthropic from "@anthropic-ai/sdk";

const client = new Anthropic({
  baseURL: "${anthropicUrl}",
  ${anthropicTsKey}: "${key}",
});
const message = await client.messages.create({
  model: "${models.anthropic}",
  max_tokens: 1024,
  messages: [{ role: "user", content: "Say hello." }],
});`,
    },
    {
      id: "claude-code",
      label: "Claude Code",
      // ANTHROPIC_AUTH_TOKEN, not ANTHROPIC_API_KEY: the gateway takes the key
      // only as a Bearer token.
      code: `export ANTHROPIC_BASE_URL="${anthropicUrl}"
export ANTHROPIC_AUTH_TOKEN="${key}"`,
    },
  ];
}

/** Where the agent prompt sends a coding agent: the site Docs' "Connect your
 * application", which has the same base URLs and key settings. */
export const DOCS_CONNECT_URL = "https://www.getphantm.ai/docs/#connect";

/** A prompt the Customer pastes into a coding agent (Claude Code, Cursor,
 * Codex…) working in their project, so it makes the base URL swap for them.
 * Built from sdk-setup.json, like the snippets. With the key just created it
 * carries the key, for the agent to keep in an environment variable. */
export function buildAgentPrompt(apiBase: string, key: string = KEY_PLACEHOLDER): string {
  const base = apiBase.replace(/\/+$/, "");
  const openaiUrl = base + setup.openai.path;
  const anthropicUrl = base + setup.anthropic.path;
  return `Switch this project's LLM API calls to go through Phantm, a gateway that lowers LLM costs. Follow Phantm's setup guide: ${DOCS_CONNECT_URL}

1. Find every place the code creates an OpenAI or Anthropic client (SDKs or direct HTTP calls).
2. Point each one at Phantm, using the Phantm key below in place of the provider's key:
   - OpenAI SDKs (Chat Completions and the Responses API): base URL ${openaiUrl}, with the key as the API key (${setup.openai.python_key_argument} in Python, ${setup.openai.typescript_key_option} in TypeScript).
   - Anthropic SDKs: base URL ${anthropicUrl} (no /v1), with the key as the auth token (${setup.anthropic.python_key_argument} in Python, ${setup.anthropic.typescript_key_option} in TypeScript), not as the API key.
   - Claude Code: set ANTHROPIC_BASE_URL=${anthropicUrl} and ANTHROPIC_AUTH_TOKEN to the key.
3. Read the key from an environment variable named PHANTM_API_KEY. Add it to the project's local .env (or wherever it keeps secrets), make sure that file is git-ignored, and never hard-code or commit the key.
4. Leave model names, prompts and all other code unchanged.
5. Send one test request through Phantm to confirm it works.

PHANTM_API_KEY=${key}`;
}

/** A key shortened for the screen ("gw_c86d…XC1s"): a just-created key is
 * shown whole only once, in its own dialog. */
export function maskKey(key: string): string {
  return `${key.slice(0, 7)}…${key.slice(-4)}`;
}
